import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import config from '../../config';

export default function CreatorSignup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [registrationEnabled, setRegistrationEnabled] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkRegistrationStatus();
  }, []);

  const checkRegistrationStatus = async () => {
    try {
      const response = await fetch(`${config.API_URL}/settings/registration-enabled`);
      const data = await response.json();
      setRegistrationEnabled(data.registration_enabled);
    } catch (err) {
      console.error('Failed to check registration status:', err);
      // If we can't check, assume it's enabled to avoid blocking legitimate users
      setRegistrationEnabled(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setCreating(true);

    try {
      // Register the user
      const registerRes = await fetch(`${config.API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          username: formData.username,
          password: formData.password,
          first_name: formData.firstName,
          last_name: formData.lastName
        }),
      });

      if (!registerRes.ok) {
        const errorData = await registerRes.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      const user = await registerRes.json();
      console.log('User created:', user);

      // Automatically log in the user
      const loginFormData = new URLSearchParams();
      loginFormData.append('username', formData.email);
      loginFormData.append('password', formData.password);

      const loginRes = await fetch(`${config.API_URL}/auth/jwt/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: loginFormData,
      });

      if (loginRes.ok) {
        const loginData = await loginRes.json();
        localStorage.setItem('access_token', loginData.access_token);
        localStorage.setItem('token_type', loginData.token_type || 'bearer');

        // Get user data from the /users/me endpoint using the token
        const userRes = await fetch(`${config.API_URL}/users/me`, {
          headers: {
            'Authorization': `Bearer ${loginData.access_token}`
          }
        });

        if (userRes.ok) {
          const userData = await userRes.json();
          localStorage.setItem('user_email', userData.email);
          localStorage.setItem('user_name', `${userData.first_name} ${userData.last_name}`);
          localStorage.setItem('user_id', userData.id);
        }

        // Redirect to onboarding
        navigate('/onboarding');
      } else {
        // If auto-login fails, still show success but redirect to login
        alert('Account created successfully! Please log in.');
        navigate('/creator-login');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex min-h-screen bg-black text-white justify-center items-center p-4">
        <div className="bg-gray-800 p-8 rounded-lg w-full max-w-md border border-blue-500 text-center">
          <h2 className="text-2xl font-bold text-white mb-6">Loading...</h2>
          <p className="text-gray-400">Checking registration status...</p>
        </div>
      </div>
    );
  }

  // Show registration disabled message
  if (registrationEnabled === false) {
    return (
      <div className="flex min-h-screen bg-black text-white justify-center items-center p-4">
        <div className="bg-gray-800 p-8 rounded-lg w-full max-w-md border border-red-500 text-center space-y-4">
          <h2 className="text-2xl font-bold text-red-400 mb-6">Registration Unavailable</h2>
          <div className="bg-red-900 border border-red-500 text-red-200 px-4 py-3 rounded">
            <p className="font-semibold">User registration is currently disabled.</p>
            <p className="text-sm mt-2">New account creation has been temporarily disabled by the site administrator.</p>
          </div>
          <div className="space-y-3 pt-4">
            <p className="text-sm text-gray-400">
              Already have an account?
            </p>
            <Link
              to="/creator-login"
              className="inline-block w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-600 transition"
            >
              Log In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show normal signup form if registration is enabled
  return (
    <div className="flex min-h-screen bg-black text-white justify-center items-center p-4">
      <form onSubmit={handleSignup} className="bg-gray-800 p-8 rounded-lg w-full max-w-md border border-blue-500 space-y-4">
        <h2 className="text-2xl font-bold text-center text-white mb-6">Creator Signup</h2>

        {error && (
          <div className="bg-red-900 border border-red-500 text-red-200 px-4 py-2 rounded">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <input
            className="w-full p-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-400"
            type="text"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            required
          />

          <input
            className="w-full p-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-400"
            type="text"
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </div>

        <input
          className="w-full p-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-400"
          type="text"
          name="username"
          placeholder="Username"
          value={formData.username}
          onChange={handleChange}
          required
        />

        <input
          className="w-full p-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-400"
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <input
          className="w-full p-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-400"
          type="password"
          name="password"
          placeholder="Password (min 8 characters)"
          value={formData.password}
          onChange={handleChange}
          required
          minLength="8"
        />

        <input
          className="w-full p-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-400"
          type="password"
          name="confirmPassword"
          placeholder="Confirm Password"
          value={formData.confirmPassword}
          onChange={handleChange}
          required
        />

        <button
          className="w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-600 transition disabled:bg-gray-600"
          type="submit"
          disabled={creating}
        >
          {creating ? 'Creating Account...' : 'Sign Up'}
        </button>

        <div className="space-y-2 text-center">
          <p className="text-xs text-gray-400">
            You can connect your Stripe account later from your dashboard.
          </p>
          <p className="text-sm">
            Already have an account?{' '}
            <Link to="/creator-login" className="text-blue-400 hover:text-blue-300">
              Log in
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
}
